import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Layers3, Loader2, Plus, Sparkles, Wand2 } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { createProposal, useProposalTemplates, type ProposalTemplate } from "@/hooks/use-proposals";

type TemplateEditorSection = {
  id: string;
  title: string;
  sectionType: string;
  body: string;
};

function createEditorSection(): TemplateEditorSection {
  return {
    id: crypto.randomUUID(),
    title: "",
    sectionType: "custom",
    body: "",
  };
}

function buildTemplatePreview(template: ProposalTemplate) {
  return template.sections
    .slice(0, 3)
    .map((section) => {
      const title = typeof section.title === "string" ? section.title : null;
      return title?.trim() || "Untitled section";
    });
}

export default function OfferTemplateLibraryPage() {
  const navigate = useNavigate();
  const [selectedIndustry, setSelectedIndustry] = useState<string>("all");
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isCreatingProposal, setIsCreatingProposal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateIndustry, setTemplateIndustry] = useState("");
  const [editorSections, setEditorSections] = useState<TemplateEditorSection[]>([
    createEditorSection(),
    createEditorSection(),
  ]);
  const [activeTemplate, setActiveTemplate] = useState<ProposalTemplate | null>(null);
  const [proposalTitle, setProposalTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const industryFilter = selectedIndustry === "all" ? undefined : selectedIndustry;
  const { templates, isLoading } = useProposalTemplates(industryFilter);

  const industries = useMemo(() => {
    const values = new Set<string>();
    for (const template of templates) {
      if (template.industry?.trim()) {
        values.add(template.industry);
      }
    }
    return ["all", ...Array.from(values).sort((left, right) => left.localeCompare(right))];
  }, [templates]);

  async function handleCreateTemplate() {
    setError(null);
    setSuccess(null);

    const normalizedName = templateName.trim();
    const normalizedSections = editorSections
      .map((section, index) => {
        const title = section.title.trim() || `Section ${index + 1}`;
        const body = section.body.trim();
        return {
          sectionType: section.sectionType || "custom",
          title,
          content: body
            ? { blocks: [{ type: "paragraph", text: body }] }
            : {},
          pricing: null,
          displayOrder: index,
        };
      })
      .filter((section) => section.title.trim().length > 0);

    if (!normalizedName) {
      setError("Template name is required.");
      return;
    }

    setIsSavingTemplate(true);
    try {
      await apiFetch("/crm/proposals/templates", {
        method: "POST",
        body: JSON.stringify({
          name: normalizedName,
          description: templateDescription.trim() || null,
          industry: templateIndustry.trim() || null,
          sections: normalizedSections,
        }),
      });

      setTemplateName("");
      setTemplateDescription("");
      setTemplateIndustry("");
      setEditorSections([createEditorSection(), createEditorSection()]);
      setIsTemplateEditorOpen(false);
      setSuccess("Template saved on the mounted CRM proposal seam.");
      navigate(0);
    } catch (templateError) {
      setError(templateError instanceof Error ? templateError.message : "Failed to save template");
    } finally {
      setIsSavingTemplate(false);
    }
  }

  async function handleUseTemplate() {
    if (!activeTemplate) {
      return;
    }

    setError(null);
    setSuccess(null);

    if (!customerName.trim() || !customerEmail.trim()) {
      setError("Customer name and email are required to create a proposal from a template.");
      return;
    }

    setIsCreatingProposal(true);
    try {
      const created = await createProposal({
        title: proposalTitle.trim() || undefined,
        templateId: activeTemplate.id,
        customerName: customerName.trim(),
        customerEmail: customerEmail.trim(),
      });

      setSuccess("Draft proposal created from the mounted template library seam.");
      setActiveTemplate(null);
      navigate(`/offers/${created.id}`);
    } catch (proposalError) {
      setError(proposalError instanceof Error ? proposalError.message : "Failed to create proposal");
    } finally {
      setIsCreatingProposal(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-muted)]/40 px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
            <Sparkles className="h-3.5 w-3.5 text-[var(--color-ai)]" />
            Offer template library
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">Proposal templates by industry</h1>
            <p className="mt-2 max-w-3xl text-sm text-[var(--color-muted-foreground)]">
              This surface stays on the mounted CRM proposal runtime: browse reusable offer templates, edit a new
              template in template mode, and create a draft proposal only after collecting a real customer identity.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setError(null);
            setSuccess(null);
            setIsTemplateEditorOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Create Template
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {industries.map((industry) => (
          <button
            key={industry}
            type="button"
            onClick={() => setSelectedIndustry(industry)}
            className={`rounded-full px-3 py-1.5 text-sm transition ${
              selectedIndustry === industry
                ? "bg-[var(--color-primary)] text-white"
                : "border border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)]"
            }`}
          >
            {industry === "all" ? "All industries" : industry}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-4 py-3 text-sm text-[var(--color-success)]">
          {success}
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-[24px] border-2 border-dashed border-[var(--color-border)] px-8 py-16 text-center">
          <Layers3 className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/40" />
          <p className="mt-4 text-lg font-medium text-[var(--color-foreground)]">No offer templates yet</p>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Start by creating a template in template mode, then reuse it to generate proposal drafts against the live CRM seam.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => {
            const preview = buildTemplatePreview(template);
            return (
              <article key={template.id} className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-background)] p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-[var(--color-foreground)]">{template.name}</p>
                    <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                      {template.description?.trim() || "Reusable proposal structure for repeatable service offers."}
                    </p>
                  </div>
                  <span className="rounded-full bg-[var(--color-ai)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-ai)]">
                    {template.industry?.trim() || "general"}
                  </span>
                </div>

                <div className="mt-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-muted)]/20 p-4">
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Preview</p>
                  <div className="mt-3 space-y-2">
                    {preview.length > 0 ? preview.map((title) => (
                      <div key={`${template.id}-${title}`} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                        {title}
                      </div>
                    )) : (
                      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
                        Template sections will appear here once content is added.
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {template.sections.length} section{template.sections.length === 1 ? "" : "s"}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setSuccess(null);
                      setActiveTemplate(template);
                      setProposalTitle("");
                      setCustomerName("");
                      setCustomerEmail("");
                    }}
                    className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-muted)]"
                  >
                    <Wand2 className="h-4 w-4" />
                    Use
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {isTemplateEditorOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4 py-10">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-ai)]">Template mode</p>
                <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">Create Template</h2>
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                  Save a reusable proposal structure directly on the mounted CRM template seam.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsTemplateEditorOpen(false)}
                className="rounded-full border border-[var(--color-border)] px-3 py-1 text-sm text-[var(--color-muted-foreground)]"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Template name</span>
                <input
                  value={templateName}
                  onChange={(event) => setTemplateName(event.target.value)}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                  placeholder="Premium retainer proposal"
                />
              </label>
              <label className="space-y-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Industry tag</span>
                <input
                  value={templateIndustry}
                  onChange={(event) => setTemplateIndustry(event.target.value)}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                  placeholder="beauty"
                />
              </label>
            </div>

            <label className="mt-4 block space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Description</span>
              <textarea
                value={templateDescription}
                onChange={(event) => setTemplateDescription(event.target.value)}
                className="min-h-24 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                placeholder="What this proposal structure is best used for."
              />
            </label>

            <div className="mt-6 space-y-4">
              {editorSections.map((section, index) => (
                <div key={section.id} className="rounded-2xl border border-[var(--color-border)] p-4">
                  <div className="grid gap-4 md:grid-cols-[1fr_180px]">
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-[var(--color-foreground)]">Section title</span>
                      <input
                        value={section.title}
                        onChange={(event) => {
                          const next = [...editorSections];
                          next[index] = { ...section, title: event.target.value };
                          setEditorSections(next);
                        }}
                        className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                        placeholder={`Section ${index + 1}`}
                      />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-[var(--color-foreground)]">Type</span>
                      <select
                        value={section.sectionType}
                        onChange={(event) => {
                          const next = [...editorSections];
                          next[index] = { ...section, sectionType: event.target.value };
                          setEditorSections(next);
                        }}
                        className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                      >
                        <option value="intro">intro</option>
                        <option value="services">services</option>
                        <option value="pricing">pricing</option>
                        <option value="terms">terms</option>
                        <option value="signature">signature</option>
                        <option value="custom">custom</option>
                      </select>
                    </label>
                  </div>

                  <label className="mt-4 block space-y-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Body preview</span>
                    <textarea
                      value={section.body}
                      onChange={(event) => {
                        const next = [...editorSections];
                        next[index] = { ...section, body: event.target.value };
                        setEditorSections(next);
                      }}
                      className="min-h-28 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                      placeholder="Describe what this section should contain."
                    />
                  </label>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-between gap-3">
              <button
                type="button"
                onClick={() => setEditorSections((previous) => [...previous, createEditorSection()])}
                className="rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)]"
              >
                Add Section
              </button>
              <button
                type="button"
                disabled={isSavingTemplate}
                onClick={() => void handleCreateTemplate()}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {isSavingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Save Template
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeTemplate ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4 py-10">
          <div className="w-full max-w-xl rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-primary)]">Use template</p>
                <h2 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{activeTemplate.name}</h2>
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                  Creating a proposal draft requires a real CRM contact. This flow resolves or creates that contact before the draft is created.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTemplate(null)}
                className="rounded-full border border-[var(--color-border)] px-3 py-1 text-sm text-[var(--color-muted-foreground)]"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <label className="block space-y-2 text-sm">
                <span className="font-medium text-[var(--color-foreground)]">Proposal title</span>
                <input
                  value={proposalTitle}
                  onChange={(event) => setProposalTitle(event.target.value)}
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                  placeholder={`${activeTemplate.name} for client`}
                />
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2 text-sm">
                  <span className="font-medium text-[var(--color-foreground)]">Customer name</span>
                  <input
                    value={customerName}
                    onChange={(event) => setCustomerName(event.target.value)}
                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                    placeholder="Dana Ionescu"
                  />
                </label>
                <label className="space-y-2 text-sm">
                  <span className="font-medium text-[var(--color-foreground)]">Customer email</span>
                  <input
                    value={customerEmail}
                    onChange={(event) => setCustomerEmail(event.target.value)}
                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2"
                    placeholder="dana@example.com"
                  />
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                disabled={isCreatingProposal}
                onClick={() => void handleUseTemplate()}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {isCreatingProposal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                Create Draft Proposal
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
